import React, { useState } from 'react';
import { Container, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import axiosInstance from 'helpers/api';
import NavigationHeader from './components/NavigationHeader';
import FooterWithPolicies from './components/FooterWithPolicies';
import { BsChatDots, BsEnvelope, BsLock, BsPerson, BsEye, BsEyeSlash, BsArrowRight, BsGoogle, BsMicrosoft, BsCheckCircle } from 'react-icons/bs';
import './styles/chat-realestate.css';

type SignUpFormInputs = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
  subscribeToNewsletter: boolean;
};

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormInputs>();

  const password = watch('password');

  const onSubmit: SubmitHandler<SignUpFormInputs> = async (data) => {
    try {
      setIsLoading(true);
      
      // Create account with lead tracking
      const response = await axiosInstance.post('/auth/signup', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        source: 'chat.realestate',
        subscribeToNewsletter: data.subscribeToNewsletter,
        isPublicUser: true, // Flag for public/consumer users
      });
      
      // Create lead record for referral system
      try {
        await axiosInstance.post('/leads/create-public-lead', {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          source: 'chat.realestate',
          leadType: 'public_referral',
          subscribeToNewsletter: data.subscribeToNewsletter,
          preferences: {
            registrationDate: new Date().toISOString(),
            platform: 'chat.realestate'
          }
        });
      } catch (leadError) {
        console.error('Lead creation failed:', leadError);
        // Don't block signup if lead creation fails
      }
      
      toast.success('Account created successfully! Let\'s find your dream home.');
      
      // Auto sign in after signup
      if (response.data.data?.token) {
        localStorage.setItem('auth_token', response.data.data.token);
        localStorage.setItem('user_info', JSON.stringify({
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          isPublicUser: true,
          user_role: 'PUBLIC'
        }));
        
        setTimeout(() => {
          navigate('/search');
        }, 1500);
      } else {
        setTimeout(() => {
          navigate('/sign-in');
        }, 2000);
      }
      
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to create account');
      setIsLoading(false);
    }
  };

  const handleSocialSignUp = (provider: 'google' | 'microsoft') => {
    toast(`${provider} sign up coming soon!`);
  };

  const passwordStrength = (password: string): { strength: string; color: string; width: string } => {
    if (!password) return { strength: '', color: '', width: '0%' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    const strengthMap = {
      1: { strength: 'Weak', color: '#ef4444', width: '25%' },
      2: { strength: 'Fair', color: '#f59e0b', width: '50%' },
      3: { strength: 'Good', color: '#3b82f6', width: '75%' },
      4: { strength: 'Strong', color: '#10b981', width: '100%' },
    };
    
    return strengthMap[strength as keyof typeof strengthMap] || strengthMap[1];
  };

  const pwStrength = passwordStrength(password || '');

  return (
    <div className="chat-realestate-wrapper">
      <NavigationHeader />
      
      <div className="signup-container">
        <div className="signup-content">
            <div className="signup-card">
              {/* Header */}
              <div className="text-center mb-5">
                <div className="signup-icon-wrapper mb-4">
                  <div className="signup-icon">
                    <BsChatDots size={32} />
                  </div>
                </div>
                <h2 className="signup-title">Create Your Account</h2>
                <p className="signup-subtitle">
                  Join thousands finding their dream homes with AI
                </p>
              </div>

              {/* Benefits List */}
              <div className="signup-benefits mb-4">
                <div className="benefit-item">
                  <BsCheckCircle className="benefit-icon" />
                  <span>AI-powered property recommendations</span>
                </div>
                <div className="benefit-item">
                  <BsCheckCircle className="benefit-icon" />
                  <span>Save searches and get instant alerts</span>
                </div>
                <div className="benefit-item">
                  <BsCheckCircle className="benefit-icon" />
                  <span>Free home valuation tools</span>
                </div>
              </div>

              {/* Social Sign Up Options */}
              <div className="social-login-section mb-4">
                <button
                  type="button"
                  className="social-login-btn google-btn w-100 mb-3"
                  onClick={() => handleSocialSignUp('google')}
                  style={{ 
                    background: '#ffffff', 
                    color: '#000000', 
                    border: '2px solid #000000',
                    fontWeight: 700 
                  }}
                >
                  <BsGoogle />
                  <span>Sign up with Google</span>
                </button>
                <button
                  type="button"
                  className="social-login-btn microsoft-btn w-100"
                  onClick={() => handleSocialSignUp('microsoft')}
                  style={{ 
                    background: '#ffffff', 
                    color: '#000000', 
                    border: '2px solid #000000',
                    fontWeight: 700 
                  }}
                >
                  <BsMicrosoft />
                  <span>Sign up with Microsoft</span>
                </button>
              </div>

              {/* Divider */}
              <div className="auth-divider">
                <span>or sign up with email</span>
              </div>

              {/* Sign Up Form */}
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label-enhanced">
                        <BsPerson className="me-2" />
                        First Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="John"
                        className="form-control-enhanced"
                        {...register('firstName', {
                          required: 'First name is required',
                        })}
                        isInvalid={!!errors.firstName}
                      />
                      {errors.firstName && (
                        <Form.Control.Feedback type="invalid">
                          {errors.firstName.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label-enhanced">
                        <BsPerson className="me-2" />
                        Last Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Doe"
                        className="form-control-enhanced"
                        {...register('lastName', {
                          required: 'Last name is required',
                        })}
                        isInvalid={!!errors.lastName}
                      />
                      {errors.lastName && (
                        <Form.Control.Feedback type="invalid">
                          {errors.lastName.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label className="form-label-enhanced">
                    <BsEnvelope className="me-2" />
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="your@email.com"
                    className="form-control-enhanced"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    isInvalid={!!errors.email}
                  />
                  {errors.email && (
                    <Form.Control.Feedback type="invalid">
                      {errors.email.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="form-label-enhanced">
                    <BsLock className="me-2" />
                    Password
                  </Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      className="form-control-enhanced"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                      })}
                      isInvalid={!!errors.password}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <BsEyeSlash /> : <BsEye />}
                    </button>
                  </div>
                  {errors.password && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {errors.password.message}
                    </Form.Control.Feedback>
                  )}
                  
                  {password && (
                    <div className="password-strength-meter mt-2">
                      <div className="strength-bar">
                        <div 
                          className="strength-bar-fill"
                          style={{ width: pwStrength.width, backgroundColor: pwStrength.color }}
                        />
                      </div>
                      <small style={{ color: pwStrength.color }}>
                        Password strength: {pwStrength.strength}
                      </small>
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="form-label-enhanced">
                    <BsLock className="me-2" />
                    Confirm Password
                  </Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      className="form-control-enhanced"
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) =>
                          value === password || 'Passwords do not match',
                      })}
                      isInvalid={!!errors.confirmPassword}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <BsEyeSlash /> : <BsEye />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {errors.confirmPassword.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="agreeToTerms"
                    label={
                      <span>
                        I agree to the{' '}
                        <Link to="/terms-of-service" target="_blank">
                          Terms of Service
                        </Link>
                        ,{' '}
                        <Link to="/privacy-policy" target="_blank">
                          Privacy Policy
                        </Link>
                        , and{' '}
                        <Link to="/ai-ethics" target="_blank">
                          AI Ethics Policy
                        </Link>
                        . I understand that de-identified, aggregated interaction data may be used to improve AI services as described in the Privacy Policy.
                      </span>
                    }
                    className="agree-terms-check"
                    {...register('agreeToTerms', {
                      required: 'You must agree to the terms',
                    })}
                    isInvalid={!!errors.agreeToTerms}
                  />
                  {errors.agreeToTerms && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {errors.agreeToTerms.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="subscribeToNewsletter"
                    label="Send me helpful tips and market updates (you can unsubscribe anytime)"
                    className="newsletter-check"
                    {...register('subscribeToNewsletter')}
                  />
                </Form.Group>

                <Button
                  type="submit"
                  variant="primary"
                  className="signup-submit-btn w-100 mb-4"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create Free Account
                      <BsArrowRight className="ms-2" />
                    </>
                  )}
                </Button>
              </Form>

              {/* Sign In Link */}
              <div className="text-center">
                <p className="signin-prompt">
                  Already have an account?{' '}
                  <Link to="/sign-in" className="signin-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
        </div>
      </div>

      <FooterWithPolicies />
    </div>
  );
};

export default SignUp;